import React, { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { Loader2 } from "lucide-react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useTranslation } from 'react-i18next';

const PaymentProcessorContent = ({ stripeConnected, stripeAccountId }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const handleConnectStripe = async () => {
    if (!user) {
      alert(t('settings.mustSignIn'));
      return;
    }

    setIsLoading(true);

    try {
      const functions = getFunctions();

      if (stripeAccountId && !stripeConnected) {
        // Resume onboarding for existing account
        const createAccountLink = httpsCallable(functions, 'createStripeConnectAccountLink');
        const result = await createAccountLink({ accountId: stripeAccountId });
        const data = result.data;

        if (data.success && data.onboardingUrl) {
          window.location.href = data.onboardingUrl;
        } else {
          throw new Error(data.error || 'Failed to create onboarding link');
        }
      } else {
        // Create new Stripe Connect account
        const createAccount = httpsCallable(functions, 'createStripeConnectAccount');
        const result = await createAccount({
          userId: user.uid,
          email: user.email,
          businessName: user.displayName || '',
        });
        const data = result.data;

        if (data.success && data.onboardingUrl) {
          // Store in both sessionStorage and localStorage for persistence
          sessionStorage.setItem('stripeConnectAccountId', data.accountId);
          localStorage.setItem('stripeConnectAccountId', data.accountId);
          sessionStorage.setItem('stripeConnectUserId', user.uid);
          localStorage.setItem('stripeConnectUserId', user.uid);

          window.location.href = data.onboardingUrl;
        } else {
          throw new Error(data.error || 'Failed to create Stripe account');
        }
      }
    } catch (error) {
      console.error('Stripe Connect error:', error);
      alert(error.message || t('settings.stripeConnectError'));
      setIsLoading(false);
    }
  };

  return (
    <div className="payment-processor-container">
      <div className="connected-processors">
        <label className="block mb-2 font-medium text-sm text-grey-900">{t('settings.connectedProcessors')}</label>
        {stripeConnected ? (
          <div className="flex gap-3 items-center border border-primary-300 bg-primary-25 rounded-lg px-4 py-2.5">
            <svg className="h-5 w-5 text-[#635BFF]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
            </svg>
            <span className="text-sm text-primary-700 font-medium">{t('settings.connectedToStripe')}</span>
            <svg className="h-4 w-4 text-primary-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        ) : (
          <div className="space-y-2">
            <button
              onClick={handleConnectStripe}
              disabled={isLoading}
              className="flex items-center gap-2.5 px-4 py-2.5 bg-[#635BFF] hover:bg-[#5851EA] disabled:opacity-70 disabled:cursor-not-allowed text-white font-medium text-sm rounded-lg transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('settings.connectingToStripe')}
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                  </svg>
                  {t('settings.connectToStripe')}
                </>
              )}
            </button>
            <p className="text-xs text-grey-500">{t('settings.stripeRequired')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentProcessorContent;
